import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Send, Star, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export default function FeedbackDialog({ open, onOpenChange }) {
  const { user } = useAuth();
  const location = useLocation();
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [message, setMessage] = useState("");
  const [replyEmail, setReplyEmail] = useState("");
  const [sending, setSending] = useState(false);

  const reset = () => {
    setRating(0);
    setHoverRating(0);
    setMessage("");
    setReplyEmail("");
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    const text = message.trim();
    if (!text) {
      toast.error("Please add a short message.");
      return;
    }
    setSending(true);
    try {
      await api.post("/feedback", {
        message: text,
        rating: rating || null,
        page: location.pathname,
        email: user ? null : (replyEmail.trim() || null),
      });
      toast.success("Thank you — your feedback is on its way!");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send feedback");
    } finally {
      setSending(false);
    }
  };

  const shownRating = hoverRating || rating;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="rounded-3xl max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: 'Manrope' }} className="tracking-tight flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" strokeWidth={1.5} />
            Please… PLEASE! Help us improve.
          </DialogTitle>
          <DialogDescription>
            This app is under active development and your feedback shapes what comes next.
            Tell us anything — what worked, what felt off, what's missing.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-sm">How was your experience?</Label>
            <div className="flex items-center gap-1" data-testid="feedback-rating">
              {[1, 2, 3, 4, 5].map((n) => {
                const active = n <= shownRating;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n === rating ? 0 : n)}
                    onMouseEnter={() => setHoverRating(n)}
                    onMouseLeave={() => setHoverRating(0)}
                    className="p-1 rounded-full transition-colors"
                    aria-label={`Rate ${n} out of 5`}
                    data-testid={`feedback-star-${n}`}
                  >
                    <Star
                      className={`w-6 h-6 transition-colors ${active ? "text-primary" : "text-muted-foreground/40"}`}
                      fill={active ? "currentColor" : "none"}
                      strokeWidth={1.5}
                    />
                  </button>
                );
              })}
              {rating > 0 && (
                <span className="ml-2 text-xs text-muted-foreground">{rating} / 5</span>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-message" className="text-sm">Your message</Label>
            <Textarea
              id="feedback-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="What would make this more useful for you?"
              rows={5}
              className="rounded-xl resize-none"
              maxLength={4000}
              data-testid="feedback-message"
              required
              autoFocus
            />
            <p className="text-xs text-muted-foreground text-right">{message.length}/4000</p>
          </div>

          {!user && (
            <div className="space-y-2">
              <Label htmlFor="feedback-email" className="text-sm">
                Email <span className="text-muted-foreground font-normal">(optional — so we can reply)</span>
              </Label>
              <Input
                id="feedback-email"
                type="email"
                value={replyEmail}
                onChange={(e) => setReplyEmail(e.target.value)}
                placeholder="you@example.com"
                className="rounded-xl h-11"
                data-testid="feedback-email"
              />
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-2 pt-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              className="rounded-full"
              disabled={sending}
              data-testid="feedback-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={sending || !message.trim()}
              className="rounded-full"
              data-testid="feedback-submit"
            >
              {sending ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sending…</>
              ) : (
                <><Send className="w-4 h-4 mr-1" strokeWidth={1.5} /> Send feedback</>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
